import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

export enum Place {
  EKEBERG = 'ekeberg',
  NORDSTRAND = 'nordstrand',
  SONDRENORDSTRAND = 'sondrenordstrand',
  LAMBERTSETER = 'lambertseter',
  OSTENSJO = 'ostensjo',
  ALLE = 'alle'
}
interface DefaultFilterArgs {
  place?: string;
  excludeFirst?: boolean;
  excludeSlug?: string;
  id?: number;
}

interface GetInterviewsFilterArgs extends DefaultFilterArgs {
  excludeFirst?: boolean;
  duplicate?: boolean;
}

interface GetPaginatedInterviewsFilterArgs extends DefaultFilterArgs {
  page: number;
  limit?: number;
  excludeFirst?: boolean;
  excludeSlug?: string;
}

interface GetInterviewFilterArgs extends DefaultFilterArgs {}

export interface ISeo {
  title: string;
  section: string;
  description: string;
}

export interface IMainPage {
  image?: string;
  imageOpacity?: number;
  title?: string;
  description: string;
  showSteps: boolean;
  pathname: string;
}

export interface IInterview {
  id: number;
  image: string;
  imageHighlight: string;
  imageOverlay: string;
  video?: string;
  title?: string;
  ingress?: string;
  quote?: string;
  content?: string;
  contentTwo?: string;
  slug: string;
  link: string;
  card: {
    title: string;
    description: string;
    buttonLabel?: string;
  };
  seo: ISeo;
}

export interface IPanorama {
  image?: string;
  imageOpacity?: number;
  title?: string;
  description: string;
  showSteps: boolean;
  pathname: string;
}

interface IInterviewContext {
  main: () => IMainPage | null;
  interviews: (args: GetInterviewsFilterArgs) => IInterview[];
  getInterview: () => IInterview | null;
  getFirstInterview: (args: DefaultFilterArgs) => IInterview | null;
  getPaginatedInterviews: (
    args: GetPaginatedInterviewsFilterArgs
  ) => IInterview[];
  /*   getInterviewPageType: () => string; */
  getInterviewCountByType: (args: DefaultFilterArgs) => number;
}

export const PanoramaContext = React.createContext<IInterviewContext>({
  main: () => null,
  interviews: () => [],
  getInterview: () => null,
  getFirstInterview: () => null,
  getPaginatedInterviews: () => [],
  /*   getInterviewPageType: () => '', */
  getInterviewCountByType: () => 0
});

export const PanoramaContextProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const params = useParams<{ type?: string; slug?: string; place?: string }>();
  const location = useLocation();
  const [activeWord, setActiveWord] = useState<string>();

  history.listen(() => {
    if (!location?.search?.includes(`?map`)) {
      window.scroll(0, 0);
    }
  });

  interface IMainPage {
    /*    type: InterviewPageType; */
    image?: string;
    imageOpacity?: number;
    title?: string;
    description: string;
    showSteps: boolean;
    pathname: string;
    place?: Place;
  }

  interface IInterview {
    id: number;
    interviewTitle: string;
    duplicate?: boolean;
    /*     type: InterviewPageType; */
    interviewIntro?: string;
    metaDescription?: string;
    image: string;
    imageHighlight: string;
    imageSoMe: string;
    imageOverlay: string;
    video?: string;
    title?: string;
    ingress?: string;
    quote?: string;
    content?: string;
    contentTwo?: string;
    bottomContent?: string;
    brokerProfile?: string;
    slug: string;
    link: string;
    place?: Place;
    card: {
      title: string;
      description: string;
      buttonLabel?: string;
    };
    seo: ISeo;
  }
  interface ISeo {
    title: string;
    description: string;
  }
  interface IInterviewContext {
    main: () => IMainPage | null;
    interviews: (args: GetInterviewsFilterArgs) => IInterview[];
    getInterview: (args: GetInterviewFilterArgs) => IInterview[] | null;
    getFirstInterview: () => IInterview | null;

    getPaginatedInterviews: (
      args: GetPaginatedInterviewsFilterArgs
    ) => IInterview[];
    /*     getInterviewPageType: () => string; */
    getInterviewCountByType: () => number;
  }

  const main: IMainPage[] = [
    {
      place: Place.EKEBERG,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/omrade_ekeberg_utsikt.jpg',
      imageOpacity: 0.6,
      title: 'Det er rom for alle på Ekeberg og Bekkelaget',
      description:
        '<p>Lengst nord i Nordstrand bydel, sørøst for Bjørvika og Gamlebyen finner vi historiske Ekeberg. Hva gjør at folk trekkes mot dette området? Her deler meglerne av sin erfaring.</p>',
      showSteps: false,
      pathname: '/kampanje/panorama/ekeberg'
    },
    {
      place: Place.NORDSTRAND,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_place.jpg',
      imageOpacity: 0.6,
      title: 'NORDSTRAND',
      description:
        '<p>Nordstrand er et stort boligstrøk som omfatter underområdene Ljabru, Sæter, Bråten, Nordseter, Nordstrandhøgda, Kastellet og Munkerud. Her deler meglerne sitt syn på et av Oslos mest attraktive områder.</p>',
      showSteps: false,
      pathname: '/kampanje/panorama/nordstrand'
    },
    {
      place: Place.SONDRENORDSTRAND,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre_nordstrand_place.jpg',
      imageOpacity: 0.6,
      title: 'SØNDRE - NORDSTRAND',
      description:
        '<p>Søndre Nordstrand er Oslos største bydel etter areal, og omfatter blant annet boligområdene Holmlia, Mortensrud, Dal/Brenna. Hvilket potensial har dette området? Her deler meglerne sine meninger.</p>',
      showSteps: false,
      pathname: '/kampanje/panorama/sondrenordstrand'
    },

    {
      place: Place.OSTENSJO,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Østensjø_Manglerud_Bøler/Ostensjo_place.jpg',
      imageOpacity: 0.6,
      title: 'ØSTENSJØ',
      description:
        '<p>I ytre by sør mellom E6 og Østmarka finner vi Østensjø. Bydelen består av de tidligere bydelene Manglerud, Bøler og Østensjø. Her deler meglerne hva de mener dette området har i vente i tiden som kommer. </p>',
      showSteps: false,
      pathname: '/kampanje/panorama/ostensjo'
    },
    {
      place: Place.LAMBERTSETER,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_place.jpg',
      imageOpacity: 0.6,
      title: 'LAMBERTSETER',
      description:
        '<p>Lambertseter blir ofte betegnet som Norges første drabantby, og har siden 2004 tilhørt bydel Nordstrand. Her deler meglerne hva de mener gjør dette området til en god investering.</p>',
      showSteps: false,
      pathname: '/kampanje/panorama/lambertseter'
    },
    {
      place: Place.ALLE,
      image: '',
      imageOpacity: 0.6,
      title: 'PANORAMA INNSIKT',
      description:
        '<p>PrivatMegleren Panorama dekker flere av Oslos mest attraktive boområder. Her deler vi innsikt som kan komme til nytte både for deg som eier, og deg som ønsker å eie bolig i bydelene Nordstrand, Søndre Nordstrand og Østensjø. Klikk på området du vil lære mer om.</p>',
      showSteps: false,
      pathname: '/kampanje/panorama/alle'
    }
  ];

  const interviews: IInterview[] = [
    {
      id: 1,
      place: Place.EKEBERG,
      interviewTitle: '',
      seo: {
        title: 'Beliggenhet, beliggenhet, beliggenhet gjelder fremdeles',
        description:
          'Til tross for klisjeen opplever eiendomsmegler Anders at nettopp beliggenheten fremdeles betyr mye for de som ønsker å bo på Nordstrand.'
      },
      card: {
        title: 'Beliggenhet, beliggenhet, beliggenhet gjelder fremdeles',
        description:
          'Til tross for klisjeen, opplever eiendomsmegler Anders at nettopp beliggenheten fremdeles betyr mye for de som ønsker å bo på Nordstrand.',
        buttonLabel: 'Les mer'
      },
      title: ' Beliggenhet, beliggenhet, beliggenhet gjelder fremdeles',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Anders Hartwig Thoresen</p>`,
      quote: '',

      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Anders_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Det er mange som er spent på utfallet av kommunens arbeid med ny småhusplan, og flere sitter litt på gjerdet og ser an hva som vil skje. For fremtiden tror jeg likevel at de fleste som ønsker bolig i dette området vil være opptatt av beliggenhet, beliggenhet, beliggenhet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>Det er noen trender som går igjen i de fleste typer boliger for tiden, og det handler om at mange er opptatt av det siste innen byggeteknikk, miljø, planløsning og interiørvalg. Énstavsparkett og vannbåren varme er gode eksempler på hva mange ønsker seg nå, i tillegg til boliger som har lav energiutnyttelse.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
        <p>Nordstrand er en av de største bydelene i areal, og har mye fint å by på – fra sjøbad i fjorden, til skogsturer i marka gjennom hele året. I tillegg til servicetilbud og nærhet til sentrum.
        </p>
        `,
      contentTwo: `
        <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Anders.jpg" alt="intervju-bilde" /><h4 class="title-margin"style=font-size:1.5rem></h4>
        <h4 class="title-margin"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>Det har kommet – og kommer stadig – flere nye aktører innen mat og drikke. Ved Sæter åpnet det for litt siden et lokalt kjøttutsalg, og nylig åpnet Gutta på Haugen ny filial i samme område. Slikt er spennende for matinteresserte!</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
        <p>Vi er opptatt av detaljene i ethvert boligsalg. Vi som jobber i PrivatMegleren har truffet alle typer mennesker, og håndtert alle typer bekymringer de måtte ha i salg- og kjøpsprosessen. Innsikten fra dette bruker vi i leveransegarantien til våre kunder, det vi selv hadde ønsket å få fra en profesjonell aktør. Ut over det går vi foran som en innovativ aktør som setter nye standarder for bransjen.</p>
        `,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Anders_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Anders.jpg',
      imageOverlay: '',
      slug: 'intervju1',
      link: '/kampanje/panorama/ekeberg/intervju1',
      brokerProfile: 'anders_hartwig_thoresen',
      duplicate: false
    },
    {
      id: 3,
      place: Place.LAMBERTSETER,
      interviewTitle: '',
      seo: {
        title: 'Attraktivt for folk i etableringsfasen',
        description:
          'Lambertseter tiltrekker seg stadig flere førstegangskjøpere.'
      },
      card: {
        title: 'Attraktivt for folk <br> i etableringsfasen',
        description:
          'Lambertseter tiltrekker seg stadig flere førstegangskjøpere.',
        buttonLabel: 'Les mer'
      },
      title: 'Attraktivt for folk i etableringsfasen',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Øystein Opheim</p>`,
      quote: '',
      content: `
        <img class="inline-image" src='https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Oystein_main.jpg' alt="intervju-bilde" />
        <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>– Det har vært et velfungerende og bra marked gjennom hele året, med nokså kort salgstid.
        Interessen har vært stor for to- og treromsleiligheter, som Lambertseterområdet består mye av.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>– Mer enn tidligere er det førstegangskjøpere som har fått øynene opp for området, som
        kjennetegnes av store grøntområder og gode turmuligheter, men likevel er sentrumsnært og
        sentralt.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
        <p>– Lambertseter er et veletablert og hyggelig område der veldig mange forblir boende. Alt man
        behøver av servicetilbud i hverdagen er lett tilgjengelig, med god tilgang til offentlig
        kommunikasjon som buss og T-bane når man trenger det.</p>
        `,
      contentTwo: `
        <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_Oystein-SEO.jpg" alt="intervju-bilde" />
        <h4 class="title-margin"style=font-size:1.5rem>Hvem passer området for?</h4>
        <p>– De som bor her representerer en god bredde av befolkningen, fra førstegangskjøpere, til
        småbarnsfamilier og godt voksne. Mange av de som flyttet hit da de første blokkene i området
        ble ferdigstilt på 50-tallet bor her fremdeles – det vitner om et godt og trivelig bomiljø.</p>
     
        <h4 class="title-margin"style=font-size:1.5rem>Har du tips til friluftsmuligheter eller aktiviteter i området?</h4>
        <p>– Det går en fin turløype rundt sørsiden av Lambertseter. Derfra er det enkelt å komme seg
        videre til Østensjøvannet og videre inn i Østmarka. Det finnes blant annet også friidrettsklubb
        og svømmeklubb i området som holder høyt nivå.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
        <p>– Vi ser det unike ved hver eiendom, og skreddersyr salgsprosessen for hvert enkelt objekt –
        uavhengig av om det er en mindre leilighet eller et stort hus.</p>
        `,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Oystein_main.jpg',
      imageHighlight:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Oystein_SEO-HIGHTLIGHT.jpg',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_Oystein-SEO.jpg',
      imageOverlay: '',
      slug: 'intervju3',
      link: '/kampanje/panorama/lambertseter/intervju3',
      brokerProfile: 'øystein_opheim',
      duplicate: false
    },
    {
      id: 4,
      place: Place.LAMBERTSETER,
      interviewTitle: '',
      seo: {
        title: 'Lambertseter er et lite samfunn i seg selv',
        description:
          'Området har utviklet seg mye i løpet av årene Line har solgt boliger på Lambertseter.'
      },
      card: {
        title: 'Lambertseter er et lite samfunn <br> i seg selv',
        description:
          'Området har utviklet seg mye i løpet av årene Line har solgt boliger på Lambertseter.',
        buttonLabel: 'Les mer'
      },
      title: 'Lambertseter er et lite samfunn i seg selv',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Line Doksum</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Line_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin block"style=font-size:1.5rem; d>Hva skjer i nærområdet nå?</h4>
        <p>Utviklingen på Lambertseter har vært veldig bra de seneste årene. Det bygges stadig nye boliger, med alle service- og aktivitetstilbud som hører til. Området blir med det mer og mer attraktivt for hvert år som går.</p>
        <h4 class="title-margin block"style=font-size:1.5rem;>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
        <p>Det er et lite samfunn i seg selv. På Lambertseter har man virkelig alt man behøver, med både kino, kjøpesenter og kollektivtilbud. I tillegg er det kort vei til rekreasjonstilbud innendørs og utendørs, og til Oslo sentrum.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Har du tips til friluftsmuligheter eller aktivitetstilbud i området?</h4>
        <p>Det er mange tilbud for enhver smak innen kort avstand. Skulpturparken på Ekeberg og Lambertseter Stadion med løpebane og svømmehall er lett tilgjengelig. Plasseringen midt mellom fjorden og skogen gjør det enkelt å komme seg til Hvervenbukta, om man ønsker et fjordbad, eller til Østmarka om man heller vil nyte skogens ro.</p>
        `,
      contentTwo: `
        <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_Line.jpg" alt="intervju-bilde" />
        <h4 class="title-margin block"style=font-size:1.5rem>Har du noen gjetning eller spådom for områdets fremtid (15-20 år?)</h4>
        <p>Jeg tror at det å bo på Lambertseter om 15-20 år blir som å bo på Grünerløkka i dag. Det vil oppleves som like sentrumsnært som Tøyen, Grønland og Løkka på sikt, med alle de samme tilbudene og aktivitetene.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
        <p>Jeg har solgt eiendommer på Lambertseter i snart ti år, og kjenner området som min egen baklomme. Hvert salg betyr mye for meg, og jeg gir derfor full innsats i hver eneste bolig jeg får tilliten til å være megler for.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Line_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_Line.jpg',
      imageOverlay: '',
      slug: 'intervju4',
      link: '/kampanje/panorama/lambertseter/intervju4',
      brokerProfile: 'line_doksum',
      duplicate: false
    },
    {
      id: 5,
      place: Place.LAMBERTSETER,
      interviewTitle: '',
      seo: {
        title: 'Et ønske om mer plass til å leve og bo',
        description:
          'Eiendomsmegler Kathrine opplever at stadig flere flytter til Lambertseter fordi de verdsetter bedre plass til livet i og rundt boligen.'
      },
      card: {
        title: 'Et ønske om mer plass til <br> å leve og bo',
        description:
          'Eiendomsmegler Kathrine opplever at stadig flere flytter til Lambertseter fordi de verdsetter bedre plass til livet i og rundt boligen.',
        buttonLabel: 'Les mer'
      },
      title: 'Et ønske om mer plass til å leve og bo',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Kathrine Pettersen</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Kathrine_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin block" style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>Etter at vi har vært gjennom en pandemi, og mange har jobbet hjemmefra, ser vi at den ekstra
        plassen i boligen er verdifull. Mer av livet foregår i boligen, og da ønsker mange å ha det
        romslig – innendørs som utendørs.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
        <p>Mange kjenner hverandre, og har gjennom det etablert et godt naboskap. De som har barn
        forteller at det er trygt å slippe ut barna for å leke sammen med venner fra blokkene rundt de
        store grøntområdene.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>I Langbølgen åpnet Piece of Cake under pandemien. Tøff start, men de har klart seg veldig
        bra. Mye av grunnen er at de har fantastiske bakverk og kaker i kafeen på dagtid, og pizza, øl
        og vin på kvelden.</p>
        `,
      contentTwo: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_Kathrine.jpg" alt="intervju-bilde" />
        <h4 class="title-margin block" style=font-size:1.5rem>Er det en spesiell person som gjør mye for lokalmiljøet som du ønsker å gi en hyllest til?</h4>
        <p>Beboere i Pynten borettslag har fortalt at de har en helt fantastisk vaktmester. En ildsjel som
        bor der selv, og som gjør mye mer enn det som forventes for at det skal være fint å bo der.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
        <p>Det er primært tiden vi legger ned i hvert enkelt oppdrag. Vi er tilgjengelige og følger nøye
        opp det som kan være en stressende situasjon, slik at prosessen blir god og trygg for både
        selger og kjøper.</p> 
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Kathrine_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Lambertseter/Lambertseter_Kathrine.jpg',
      imageOverlay: '',
      slug: 'intervju5',
      link: '/kampanje/panorama/lambertseter/intervju5',
      brokerProfile: 'kathrine_pettersen',
      duplicate: false
    },
    {
      id: 6,
      place: Place.SONDRENORDSTRAND,
      interviewTitle: '',
      seo: {
        title: 'Oslofolk har fått øynene opp for Søndre Nordstrand',
        description:
          'Eiendomsmegler Anya mener det mangfoldige og inkluderende miljøet gjør at bydelen er stadig mer ettertraktet.'
      },
      card: {
        title: 'Oslofolk har fått øynene opp for <br> Søndre Nordstrand',
        description:
          'Eiendomsmegler Anya mener det mangfoldige og inkluderende miljøet gjør at bydelen er stadig mer ettertraktet.',
        buttonLabel: 'Les mer'
      },
      title: 'Oslofolk har fått øynene opp for Søndre Nordstrand',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Anya Martinsen</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Anya_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Vi har det siste halvåret opplevd sterkere vekst og større etterspørsel enn tidligere. Mange ser etter boliger over flere plan, der det er nok plass til å etablere flere sosiale soner for store og små. Derfor er rekkehus i området stadig mer ettertraktet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan har utviklingen vært den siste tiden?</h4>
        <p>I det siste har Oslofolk fått øynene opp for de ulike nabolagene i Søndre Nordstrand, og med det er det også mange som beveger seg hit fra andre deler av byen.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
        <p>Søndre Nordstrand er en av bydelene med høyest andel interne flyttinger – noe som er et tegn på at de som bor her trives i området. Omgivelsene er jordnære og naturskjønne, med kort vei til opplevelser langs fjorden og i marka. Bydelen er kjent for et mangfoldig og inkluderende miljø som passer like godt for folk i etableringsfasen, som for godt voksne.</p>
       
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Anya.jpg" alt="intervju-bilde" />
      <h4 class="title-margin block "style=font-size:1.5rem>Har du noen gjetning eller spådom for områdets fremtid (15-20 år)</h4>
      <p>Vi ser at Oslo sentrum ekspanderer, og at områdene rundt blir mer og mer inkludert i sentrum av byen. Jeg håper Søndre Nordstrand vil beholde småbypreget i årene som kommer – gjerne med flere små sentrumsområder med ulike publikumstilbud der folk kan samles.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Fra kundene mine gjennom de siste åtte årene som eiendomsmegler i Søndre Nordstrand, får jeg høre at jeg oppleves som ærlig og tillitsfull. Jeg ønsker kunden en trygg og komfortabel prosess der vi gjennom åpen kommunikasjon og godt samarbeid fra A til Å oppnår den beste prisen for boligen.</p>
        
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Anya_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Anya.jpg',
      imageOverlay: '',
      slug: 'intervju6',
      link: '/kampanje/panorama/sondrenordstrand/intervju6',
      brokerProfile: 'anya_martinsen',
      duplicate: false
    },
    {
      id: 7,
      place: Place.NORDSTRAND,
      interviewTitle: '',
      seo: {
        title: 'Bolig på Nordstrand er fortsatt en god investering',
        description:
          'Magnus har solgt boliger på Nordstrand og Bekkelaget i årevis. Selv mener han at dette er det fineste området i byen.'
      },
      card: {
        title: 'Bolig på Nordstrand er fortsatt <br> en god investering',
        description:
          'Magnus har solgt boliger på Nordstrand og Bekkelaget i årevis. Selv mener han at dette er det fineste området i byen.',
        buttonLabel: 'Les mer'
      },
      title: 'Bolig på Nordstrand er fortsatt en god investering',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Magnus Dahl</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Magnus_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin block"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Siden nyttår har det kommet mange nye boliger på markedet, og interessen har vært stor. Selv
        om kommunen for tiden arbeider med ny småhusplan, mener jeg at investering i bolig på
        Nordstrand på lang sikt fortsatt er en trygg investering.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
        <p>Nordstrand er et veldig bra sted å vokse opp. Her er gode og trygge nabolag skjermet fra tungt
        trafikkerte veier, med nærhet til både sjø og skog. Nordstrand er også kjent for et allsidig
        idrettsmiljø for store og små.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>Som gammel håndballspiller må jeg trekke frem Nordstrand Idrettsforening, som betød mye
        for meg i min oppvekst. Nå er det åpnet en ny hall for breddeidrett i Nordstrandhallen, med
        tilhørende kafé der gamle og unge lokale idrettsfolk møtes for kaffe og lunsj. Ellers er det
        også etablert arena for e-sport ved Nordstrandhallen, noe som er veldig spennende!</p>

        `,
      contentTwo: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Magnus.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Har du noen gjetning eller spådom for områdets fremtid (15-20 år?)</h4>
      <p>Jeg håper at Nordstrand fortsatt er et godt og trygt sted for alle, med god miks av lokale
      servicetilbud, ærverdige eneboliger, rekkehus, småhus og leilighetsbygg.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
      <p>PrivatMegleren har lang fartstid i området Bekkelaget/Nordstrand, og kjenner derfor
      nabolagene veldig godt. Hos oss kan du forvente kvalitet og dedikasjon av en aktør som tar
      alle oppdrag på høyeste alvor.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Magnus_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Magnus.jpg',
      imageOverlay: '',
      slug: 'intervju7',
      link: '/kampanje/panorama/nordstrand/intervju7',
      brokerProfile: 'magnus_dahl',
      duplicate: false
    },
    {
      id: 9,
      place: Place.SONDRENORDSTRAND,
      interviewTitle: '',
      seo: {
        title: '',
        description: ''
      },
      card: {
        title: 'Attraktivt grunnet bokvalitet',
        description:
          'Nordstrand og Søndre Nordstrand vil bli en tettere del av sentrumskjernen, men likevel beholde utkantfølelsen. Det er eiendomsmegler Soliman helt sikker på.',
        buttonLabel: 'Les mer'
      },
      title: 'Stolte innbyggere styrker nærområdet',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Soliman Sarwar</p>`,
      quote: '',
      content: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Soliman.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
      <p>Netto innflytting til Stor-Oslo og byen er høyere enn tidligere. Det gjør at etterspørselen øker
      i områder som er allerede er høyt på lista hos mange. Markedet beveger seg for tiden litt
      annerledes enn før, noe som på sikt kan bidra til at flere får anledning til å kjøpe seg inn.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
      <p>Det er generelt veldig god bokvalitet på både Nordstrand og Søndre Nordstrand. Det er
      folkelige og hyggelige nabolag, med fine friluftsområder, gode skoler og bra oppvekstmiljø.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hvem passer områdene for?</h4>
      <p>Stadig flere yngre flytter til området, fordi det de senere årene er bygget en del nytt her. De
      eldre flytter i større grad innad, fra hus til leilighet. Det er et godt signal om at folk trives i
      området.</p>

      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Soliman.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Er det en spesiell person som gjør mye for lokalmiljøet som du ønsker å gi en hyllest til?</h4>
      <p>Det er de mange i nabolagene som sitter i lokale styrer, deltar med frivillig arbeid i
      idrettsmiljøet og sponser aktiviteter for barn og unge. Det er overraskende hvor mange som
      bidrar med det de kan for å holde slike tilbud i live.</p>
      <h4 class="title-margin "style=font-size:1.5rem>Hva vil skje i området de neste 5–10 årene?</h4>
      <p>Jeg tror både Søndre Nordstrand og Nordstrand kommer til å bli vekstvinnere. Området
      kommer til å bli en tettere del av sentrumskjernen i byen, men fremdeles med en følelse av
      å leve mer fritt.</p>        
      <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Selv etter alle mine år som megler elsker jeg fremdeles å gå på jobb hver dag. Jeg går dypt i
      materien, og gjør en grundig jobb for at kundene skal oppleve prosessen som god.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Soliman.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Soliman.jpg',
      imageOverlay: '',
      slug: 'intervju9',
      link: '/kampanje/panorama/sondrenordstrand/intervju9',
      brokerProfile: 'soliman_sarwar',
      duplicate: false
    },
    {
      id: 10,
      place: Place.NORDSTRAND,
      interviewTitle: '',
      seo: {
        title: 'Stolte innbyggere styrker nærområdet',
        description:
          'Soliman Sarwar går dypt i materien, og gjør en grundig jobb for at kundene skal oppleve prosessen som god hos PrivatMegleren Panorama'
      },
      card: {
        title: 'Attraktivt grunnet bokvalitet',
        description:
          'Nordstrand og Søndre Nordstrand vil bli en tettere del av sentrumskjernen, men likevel beholde utkantfølelsen. Det er eiendomsmegler Soliman helt sikker på.',
        buttonLabel: 'Les mer'
      },
      title: 'Stolte innbyggere styrker nærområdet',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Soliman Sarwar</p><h2 class="ingress"">Det er generelt veldig god bokvalitet på både Nordstrand og Søndre Nordstrand</h2>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Soliman.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Netto innflytting til Stor-Oslo og byen er høyere enn tidligere. Det gjør at etterspørselen øker
        i områder som er allerede er høyt på lista hos mange. Markedet beveger seg for tiden litt
        annerledes enn før, noe som på sikt kan bidra til at flere får anledning til å kjøpe seg inn.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
        <p>Det er generelt veldig god bokvalitet på både Nordstrand og Søndre Nordstrand. Det er
        folkelige og hyggelige nabolag, med fine friluftsområder, gode skoler og bra oppvekstmiljø.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvem passer områdene for?</h4>
        <p>Stadig flere yngre flytter til området, fordi det de senere årene er bygget en del nytt her. De
        eldre flytter i større grad innad, fra hus til leilighet. Det er et godt signal om at folk trives i
        området.</p>
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Soliman.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Er det en spesiell person som gjør mye for lokalmiljøet som du ønsker å gi en hyllest til?</h4>
      <p>Det er de mange i nabolagene som sitter i lokale styrer, deltar med frivillig arbeid i
      idrettsmiljøet og sponser aktiviteter for barn og unge. Det er overraskende hvor mange som
      bidrar med det de kan for å holde slike tilbud i live.</p>
      <h4 class="title-margin "style=font-size:1.5rem>Hva vil skje i området de neste 5–10 årene?</h4>
      <p>Jeg tror både Søndre Nordstrand og Nordstrand kommer til å bli vekstvinnere. Området
      kommer til å bli en tettere del av sentrumskjernen i byen, men fremdeles med en følelse av
      å leve mer fritt.</p>        
      <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Selv etter alle mine år som megler elsker jeg fremdeles å gå på jobb hver dag. Jeg går dypt i
      materien, og gjør en grundig jobb for at kundene skal oppleve prosessen som god.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Soliman.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Soliman.jpg',
      imageOverlay: '',
      slug: 'intervju10',
      link: '/kampanje/panorama/nordstrand/intervju10',
      brokerProfile: 'soliman_sarwar',
      duplicate: true
    },
    {
      id: 12,
      place: Place.OSTENSJO,
      interviewTitle: '',
      seo: {
        title: 'Et område hvor folk trives og blir værende',
        description:
          'Aktivitetstilbudene på Manglerud og Skullerud er kjent som veldig gode, med både organisert idrett, offentlig bad og ikke minst kort vei til rekreasjon i Østmarka, sier Bjørn-Erik Braaten i PrivatMegleren.'
      },
      card: {
        title: 'Et område hvor folk trives og blir værende',
        description:
          'Eiendomsmegler Bjørn-Erik opplever at stadig flere yngre foretrekker å etablere seg på Manglerud og Skullerud.',
        buttonLabel: 'Les mer'
      },
      title: 'Et område hvor folk trives og blir værende',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Bjørn-Erik Braaten</p><h2 class="ingress"">Aktivitetstilbudene på Manglerud og Skullerud er kjent som veldig gode</h2>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Bjorn_Erik.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>Jeg har lagt merke til at folk som ser etter ny bolig er mer opptatt av energieffektivitet og
        strømkostnader enn tidligere. Det henger nok tett sammen med prisutviklingen i samfunnet,
        og langsiktig planlegging for ny tilværelse i ny bolig.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i området?</h4>
        <p>Det er kort vei til et bredt utvalg av servicetilbud om man ønsker å shoppe, ta en kaffe, møte
        venner eller trene. Aktivitetstilbudene på Manglerud og Skullerud er kjent som veldig gode,
        med både organisert idrett, offentlig bad og ikke minst kort vei til rekreasjon i Østmarka.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvem passer dette området for?</h4>
        <p>Her er det rom for alle typer mennesker, i forskjellige livsfaser og med ulike bakgrunner. De
        siste årene har stadig flere yngre flyttet til. Snittalder på nye kjøpere det siste året er 39 år,
        mens dagens eiere har en snittalder på 51 år. Faktisk er mindre enn 10 prosent av boligene i
        området utleie, og om lag halvparten av de som kjøper ny bolig bor allerede i bydelen. Høy
        grad av flytting internt i et område er ofte en god markør for at folk trives der.</p>
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Manglerud-Bjorn_Erik.jpg"" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Har du noen gjetning eller spådom for områdets fremtid (15-20 år)?</h4>
      <p>Jeg ser for meg at boligområdene på Manglerud og Skullerud holder stand, men med
      naturlig utskifting av beboere. Skullerud har stort potensial for videre utvikling – det blir
      spennende å se hvordan det vil se ut der i fremtiden.</p>        
      <h4 class="title-margin inline"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Jeg har spesialisert meg på tett og personlig oppfølging av selgere og interessenter
      gjennom hele prosessen. Jeg er innom eiendommen flere ganger før visning og alle
      valg som tas er nøye gjennomtenkt, dette er svært viktig for å få best mulig pris for
      boligen. Ingen skal kjenne boligen din bedre enn meg – bortsett fra deg selv.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Bjorn_Erik.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Manglerud-Bjorn_Erik.jpg',
      imageOverlay: '',
      slug: 'intervju12',
      link: '/kampanje/panorama/ostensjo/intervju12',
      brokerProfile: 'bjørn-erik_braaten',
      duplicate: false
    },
    {
      id: 13,
      place: Place.NORDSTRAND,
      interviewTitle: '',
      seo: {
        title: 'Tiltalende nabolag med noe for ethvert ønske og behov.',
        description:
          'PrivatMegleren leverer skreddersydd markedsføring av eiendommen, tilpasset mottaker, basert på kunstig intelligens og maskinlæring, sier Robert Eriksen i PrivatMegleren.'
      },
      card: {
        title: 'Tiltalende nabolag med noe for ethvert ønske og behov.',
        description:
          'Eiendomsmegler Robert tror på en sunn og forsiktig vekst på Nordstrand og Bekkelaget i årene som kommer.',
        buttonLabel: 'Les mer'
      },
      title: 'Tiltalende nabolag med noe for ethvert ønske og behov.',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Robert Eriksen</p><h2 class="ingress">Sjøen og marka er like i nærheten, og servicetilbudene er utbygd og fornyet de siste årene.</h2>`,
      quote: '',
      content: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Robert.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
      <p>Hjemmekontor er kommet for å bli, så det ekstra rommet i boligen har fått stor verdi.
      Mange opplever det som spesielt attraktivt om boligen de vurderer å kjøpe er ferdig
      pusset opp.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hvem passer dette området for?</h4>
      <p>Det er i grunn tiltalende for alle. Med et godt utviklet fritidsaktivitetstilbud finner både
      barnefamilier og godt voksne noe for deres interesse. Skolekretsene i området er
      også kjent som svært gode. Sjøen og marka er like i nærheten, og servicetilbudene
      er utbygd og fornyet de siste årene.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
      <p>Jeg er ikke helt i målgruppen for det selv ennå, men jeg er blitt fortalt at det er et
      særdeles godt pensjonistmiljø med aktive foreninger på både Kastellet og Sæter.</p>
      
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height-ver-2" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Ekeberg-Robert.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Hva vil skje i området de neste 5–10 årene?</h4>
      <p>Vi kommer nok til å se en sunn, forsiktig vekst de neste årene. Området er blitt stadig
      mer fortettet i flere år, men fremover er det ikke så mye mer som kan utvikles videre.
      De store grøntområdene på Ekebergsletta og Brannfjellskogen kommer ikke til å bli
      utviklet til boliger med det aller første.</p>  
      <h4 class="title-margin inline-block" style=font-size:1.5rem;>Hva gjør PrivatMegleren annerledes?</h4>
      <p>Som det mestselgende meglerforetaket på Bekkelaget og Nordstrand er vi desidert
      størst og markedsledende. Privatmegleren leverer skreddersydd markedsføring av
      eiendommen, tilpasset mottaker, basert på kunstig intelligens og maskinlæring.</p>
      <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Jeg gjør alt håndverket selv og avdekker særegenheter ved nettopp din eiendom, og
      hva du eventuelt kan gjøre for at eiendommen blir enda mer attraktiv hos potensielle
      kjøpere. For en boligselger er det praktisk å ha én person å forholde seg til, med full
      oversikt i hele prosessen, fra god rådgivning og tilpasset tekstproduksjon, til visning
      og overtakelse.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Robert.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Ekeberg-Robert.jpg',
      imageOverlay: '',
      slug: 'intervju13',
      link: '/kampanje/panorama/nordstrand/intervju13',
      brokerProfile: 'robert_eriksen',
      duplicate: false
    },

    {
      id: 14,
      place: Place.NORDSTRAND,
      interviewTitle: '',
      seo: {
        title: 'Som et lite paradis i byen',
        description:
          'Mange opplever spesielt øyene på Nedre Bekkelaget som et lite paradis i byen, bare syv minutter fra sentrum, sier Kim Andre Krabbe i PrivatMegleren.'
      },
      card: {
        title: 'Som et lite paradis i byen',
        description:
          'Eiendomsmegler Kim André oppfatter at Nedre Bekkelaget og øyene er like populært som alltid.',
        buttonLabel: 'Les mer'
      },
      title: 'Som et lite paradis i byen',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Kim André Krabbe</p><h2 style="font-size: 28px;">Nedre Bekkelaget og øyene er et attraktivt marked med god interesse og omsetning</h2>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Kim-2.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan har utviklingen vært den siste tiden?</h4>
        <p>Nedre Bekkelaget og øyene er et attraktivt marked med god interesse og omsetning.
        Behovet for boliger øker i hele kommunen, og dette området er fortsatt tiltrekkende
        for mange. Nå for tiden bruker folk gjerne litt lengre tid på å finne den ideelle boligen
        for resten av livet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
        <p>Mange opplever spesielt øyene på Nedre Bekkelaget som et lite paradis i byen, bare
        syv minutter fra sentrum. Folk trives godt med den umiddelbare nærheten til sjøen,
        og naboskapet er upåklagelig. Alle som bor her er opptatt av at alle andre har det
        bra. Det vises også gjennom aktive velforeninger og det lokale service- og
        aktivitetstilbudet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>Det nye Bekkelagsbadet er blitt et veldig fint sted for å være aktiv og sosial. Ellers
        finner man ofte lokalbefolkningen på vår egen lille landhandel, eller på et av de
        hyggelige serveringsstedene i området. Prøv caféen på Solvik på Malmøya. Sjelden
        atmosfære.</p>
      
        `,
      contentTwo: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Bekkelaget-Kim.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
      <p>Vi har en unik verktøykasse som fungerer særdeles godt i de fleste salg. Meglerne
      våre er oppriktig engasjert i jobben sin, og bryr seg om kundene sine.</p>        
      <h4 class="title-margin" style=font-size:1.5rem;>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Jeg har jobbet som megler i 20 år, og føler meg veldig privilegert som får lov til å
      jobbe med det jeg liker aller best. Jeg brenner fortsatt for faget – jeg elsker å møte
      nye folk, stå på visning, og være en rådgiver i kjøps- og salgsprosessen.</p>

        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Kim-2.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Bekkelaget-Kim.jpg',
      imageOverlay: '',
      slug: 'intervju14',
      link: '/kampanje/panorama/nordstrand/intervju14',
      brokerProfile: 'kim_andré_krabbe',
      duplicate: false
    },
    {
      id: 15,
      place: Place.OSTENSJO,
      interviewTitle: '',
      seo: {
        title: 'En landlig, skjult perle',
        description:
          'Tilflyttere forteller at det er lett å bli en del av nærmiljøet og lokalsamfunnet, sier Stefan Safri i PrivatMegleren.'
      },
      card: {
        title: 'En landlig, skjult perle',
        description:
          'Slik forteller eiendomsmegler Stefan Safri at tilflyttere til Høyenhall og Manglerud (Østensjø) beskriver området.',
        buttonLabel: 'Les mer'
      },
      title: 'En landlig, skjult perle',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Stefan Safri</p><h2 style="font-size: 28px;">Spesielt småbarnsfamilier setter pris på trafikkstille gater, hvor barna trygt kan være ute og gode oppvekstsvilkår.</h2>`,
      quote: '',
      content: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Stefan.jpg" />
      <h4 class="title-margin block" style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
      <p>Området kjennetegnes av mye intern flytting. Mange begynner i mindre bolig og
      oppgraderer etter hvert som familien vokser og behovene endres.</p>
      <h4 class="title-margin block"style=font-size:1.5rem>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
      <p>De som kommer hit uten å kjenne til stedet fra før sier, sier at det oppleves som en landlig,
      skjult perle som likevel er sentrumsnær. Spesielt småbarnsfamilier setter pris på trafikkstille
      gater, hvor barna trygt kan være ute og gode oppvekstsvilkår.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i området?</h4>
      <p>Tilflyttere forteller at det er lett å bli en del av nærmiljøet og lokalsamfunnet. Det er mange
      fine samlingspunkter for store og små, og aktive velforeninger som arrangerer moro for barn
      og voksne.</p>
`,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Ostensjo-Stefan.jpg" alt="intervju-bilde"  />
      <h4  class="title-margin block"style=font-size:1.5rem>Har du tips til friluftsmuligheter eller aktivitetstilbud i området?</h4>
      <p>Det er flere nærsentre, godt med treningsfasiliteter, helt nytt bad og aktivitetshus og
      Østensjøvannet er like i nærheten. Videre er det kort vei til friluft og rekreasjon i Østmarka.</p>  
      <h4 class="title-margin block" style=font-size:1.5rem;>Har du noen gjetning eller spådom for områdets fremtid (15–20 år)?</h4>
      <p>Jeg håper områdene bevares mye slik de er i dag, med frodige, grønne lunger og mye natur.
      God kombinasjon av blokk- og villabebyggelse.</p>
      <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Det er fordi alt vi gjør i PrivatMegleren er tuftet på vår lokalkunnskap. Jeg er oppvokst i
      området selv, og har barn som er 5. generasjon som vokser opp her nå. Jeg har derfor
      førstehåndskunnskap om nærmiljøet og hvordan det er å bo her.</p>`,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Stefan.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ostensjo_Manglerud_Boler/Ostensjo-Stefan.jpg',
      imageOverlay: '',
      slug: 'intervju15',
      link: '/kampanje/panorama/ostensjo/intervju15',
      brokerProfile: 'stefan_safri',
      duplicate: false
    },
    {
      id: 16,
      place: Place.NORDSTRAND,
      interviewTitle: '',
      seo: {
        title: 'Beliggenhet, beliggenhet, beliggenhet gjelder fremdeles',
        description:
          'Til tross for klisjeen opplever eiendomsmegler Anders at nettopp beliggenheten fremdeles betyr mye for de som ønsker å bo på Nordstrand.'
      },
      card: {
        title: 'Beliggenhet, beliggenhet, beliggenhet gjelder fremdeles',
        description:
          'Til tross for klisjeen, opplever eiendomsmegler Anders at nettopp beliggenheten fremdeles betyr mye for de som ønsker å bo på Nordstrand.',
        buttonLabel: 'Les mer'
      },
      title: ' Beliggenhet, beliggenhet, beliggenhet gjelder fremdeles',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Anders Hartwig Thoresen</p>`,
      quote: '',

      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Anders_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Det er mange som er spent på utfallet av kommunens arbeid med ny småhusplan, og flere sitter litt på gjerdet og ser an hva som vil skje. For fremtiden tror jeg likevel at de fleste som ønsker bolig i dette området vil være opptatt av beliggenhet, beliggenhet, beliggenhet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>Det er noen trender som går igjen i de fleste typer boliger for tiden, og det handler om at mange er opptatt av det siste innen byggeteknikk, miljø, planløsning og interiørvalg. Énstavsparkett og vannbåren varme er gode eksempler på hva mange ønsker seg nå, i tillegg til boliger som har lav energiutnyttelse.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
        <p>Nordstrand er en av de største bydelene i areal, og har mye fint å by på – fra sjøbad i fjorden, til skogsturer i marka gjennom hele året. I tillegg til servicetilbud og nærhet til sentrum.
        </p>
        `,
      contentTwo: `
        <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Anders.jpg" alt="intervju-bilde" /><h4 class="title-margin"style=font-size:1.5rem></h4>
        <h4 class="title-margin"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>Det har kommet – og kommer stadig – flere nye aktører innen mat og drikke. Ved Sæter åpnet det for litt siden et lokalt kjøttutsalg, og nylig åpnet Gutta på Haugen ny filial i samme område. Slikt er spennende for matinteresserte!</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
        <p>Vi er opptatt av detaljene i ethvert boligsalg. Vi som jobber i PrivatMegleren har truffet alle typer mennesker, og håndtert alle typer bekymringer de måtte ha i salg- og kjøpsprosessen. Innsikten fra dette bruker vi i leveransegarantien til våre kunder, det vi selv hadde ønsket å få fra en profesjonell aktør. Ut over det går vi foran som en innovativ aktør som setter nye standarder for bransjen.</p>
        `,
      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Anders_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Anders.jpg',
      imageOverlay: '',
      slug: 'intervju16',
      link: '/kampanje/panorama/nordstrand/intervju16',
      brokerProfile: 'anders_hartwig_thoresen',
      duplicate: true
    },
    {
      id: 17,
      place: Place.EKEBERG,
      interviewTitle: '',
      seo: {
        title: 'Bolig på Nordstrand er fortsatt en god investering',
        description:
          'Magnus har solgt boliger på Nordstrand og Bekkelaget i årevis. Selv mener han at dette er det fineste området i byen.'
      },
      card: {
        title: 'Bolig på Nordstrand er fortsatt <br> en god investering',
        description:
          'Magnus har solgt boliger på Nordstrand og Bekkelaget i årevis. Selv mener han at dette er det fineste området i byen.',
        buttonLabel: 'Les mer'
      },
      title: 'Bolig på Nordstrand er fortsatt en god investering',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Magnus Dahl</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Magnus_main.jpg" alt="intervju-bilde" />
        <h4 class="title-margin block"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Siden nyttår har det kommet mange nye boliger på markedet, og interessen har vært stor. Selv
        om kommunen for tiden arbeider med ny småhusplan, mener jeg at investering i bolig på
        Nordstrand på lang sikt fortsatt er en trygg investering.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Hvordan vil du beskrive området for tilflyttere som ikke har vokst opp i Oslo?</h4>
        <p>Nordstrand er et veldig bra sted å vokse opp. Her er gode og trygge nabolag skjermet fra tungt
        trafikkerte veier, med nærhet til både sjø og skog. Nordstrand er også kjent for et allsidig
        idrettsmiljø for store og små.</p>
        <h4 class="title-margin block"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>Som gammel håndballspiller må jeg trekke frem Nordstrand Idrettsforening, som betød mye
        for meg i min oppvekst. Nå er det åpnet en ny hall for breddeidrett i Nordstrandhallen, med
        tilhørende kafé der gamle og unge lokale idrettsfolk møtes for kaffe og lunsj. Ellers er det
        også etablert arena for e-sport ved Nordstrandhallen, noe som er veldig spennende!</p>

        `,
      contentTwo: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Magnus.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Har du noen gjetning eller spådom for områdets fremtid (15-20 år?)</h4>
      <p>Jeg håper at Nordstrand fortsatt er et godt og trygt sted for alle, med god miks av lokale
      servicetilbud, ærverdige eneboliger, rekkehus, småhus og leilighetsbygg.</p>
      <h4 class="title-margin"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
      <p>PrivatMegleren har lang fartstid i området Bekkelaget/Nordstrand, og kjenner derfor
      nabolagene veldig godt. Hos oss kan du forvente kvalitet og dedikasjon av en aktør som tar
      alle oppdrag på høyeste alvor.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Magnus_main.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Nordstrand_Magnus.jpg',
      imageOverlay: '',
      slug: 'intervju17',
      link: '/kampanje/panorama/ekeberg/intervju17',
      brokerProfile: 'magnus_dahl',
      duplicate: true
    },
    {
      id: 19,
      place: Place.EKEBERG,
      interviewTitle: '',
      seo: {
        title: '',
        description: ''
      },
      card: {
        title: 'Som et lite paradis i byen',
        description:
          'Eiendomsmegler Kim André oppfatter at Nedre Bekkelaget og øyene er like populært som alltid.',
        buttonLabel: 'Les mer'
      },
      title: 'Som et lite paradis i byen',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Kim André Krabbe</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Kim-2.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan har utviklingen vært den siste tiden?</h4>
        <p>Nedre Bekkelaget og øyene er et attraktivt marked med god interesse og omsetning.
        Behovet for boliger øker i hele kommunen, og dette området er fortsatt tiltrekkende
        for mange. Nå for tiden bruker folk gjerne litt lengre tid på å finne den ideelle boligen
        for resten av livet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
        <p>Mange opplever spesielt øyene på Nedre Bekkelaget som et lite paradis i byen, bare
        syv minutter fra sentrum. Folk trives godt med den umiddelbare nærheten til sjøen,
        og naboskapet er upåklagelig. Alle som bor her er opptatt av at alle andre har det
        bra. Det vises også gjennom aktive velforeninger og det lokale service- og
        aktivitetstilbudet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du tips til en lokal aktør som flere burde støtte?</h4>
        <p>Det nye Bekkelagsbadet er blitt et veldig fint sted for å være aktiv og sosial. Ellers
        finner man ofte lokalbefolkningen på vår egen lille landhandel, eller på et av de
        hyggelige serveringsstedene i området. Prøv caféen på Solvik på Malmøya. Sjelden
        atmosfære.</p>
        `,
      contentTwo: `
      <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Bekkelaget-Kim.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
      <p>Vi har en unik verktøykasse som fungerer særdeles godt i de fleste salg. Meglerne
      våre er oppriktig engasjert i jobben sin, og bryr seg om kundene sine.</p>        
      <h4 class="title-margin" style=font-size:1.5rem;>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Jeg har jobbet som megler i 20 år, og føler meg veldig privilegert som får lov til å
      jobbe med det jeg liker aller best. Jeg brenner fortsatt for faget – jeg elsker å møte
      nye folk, stå på visning, og være en rådgiver i kjøps- og salgsprosessen.</p>
      `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Kim-2.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Nordstrand/Bekkelaget-Kim.jpg',
      imageOverlay: '',
      slug: 'intervju19',
      link: '/kampanje/panorama/ekeberg/intervju19',
      brokerProfile: 'kim_andré_krabbe',
      duplicate: true
    },
    {
      id: 20,
      place: Place.EKEBERG,
      interviewTitle: '',
      seo: {
        title: '',
        description: ''
      },
      card: {
        title: 'Attraktivt grunnet bokvalitet',
        description:
          'Nordstrand og Søndre Nordstrand vil bli en tettere del av sentrumskjernen, men likevel beholde utkantfølelsen. Det er eiendomsmegler Soliman helt sikker på.',
        buttonLabel: 'Les mer'
      },
      title: 'Stolte innbyggere styrker nærområdet',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Soliman Sarwar</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Soliman.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hva skjer i nærområdet nå?</h4>
        <p>Netto innflytting til Stor-Oslo og byen er høyere enn tidligere. Det gjør at etterspørselen øker
        i områder som er allerede er høyt på lista hos mange. Markedet beveger seg for tiden litt
        annerledes enn før, noe som på sikt kan bidra til at flere får anledning til å kjøpe seg inn.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i dette området?</h4>
        <p>Det er generelt veldig god bokvalitet på både Nordstrand og Søndre Nordstrand. Det er
        folkelige og hyggelige nabolag, med fine friluftsområder, gode skoler og bra oppvekstmiljø.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvem passer områdene for?</h4>
        <p>Stadig flere yngre flytter til området, fordi det de senere årene er bygget en del nytt her. De
        eldre flytter i større grad innad, fra hus til leilighet. Det er et godt signal om at folk trives i
        området.</p>
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Soliman.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Er det en spesiell person som gjør mye for lokalmiljøet som du ønsker å gi en hyllest til?</h4>
      <p>Det er de mange i nabolagene som sitter i lokale styrer, deltar med frivillig arbeid i
      idrettsmiljøet og sponser aktiviteter for barn og unge. Det er overraskende hvor mange som
      bidrar med det de kan for å holde slike tilbud i live.</p>
      <h4 class="title-margin "style=font-size:1.5rem>Hva vil skje i området de neste 5–10 årene?</h4>
      <p>Jeg tror både Søndre Nordstrand og Nordstrand kommer til å bli vekstvinnere. Området
      kommer til å bli en tettere del av sentrumskjernen i byen, men fremdeles med en følelse av
      å leve mer fritt.</p>        
      <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>Selv etter alle mine år som megler elsker jeg fremdeles å gå på jobb hver dag. Jeg går dypt i
      materien, og gjør en grundig jobb for at kundene skal oppleve prosessen som god.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Soliman.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Sondre_Nordstrand/Sondre-Nordstrand_Soliman.jpg',
      imageOverlay: '',
      slug: 'intervju20',
      link: '/kampanje/panorama/ekeberg/intervju20',
      brokerProfile: 'soliman_sarwar',
      duplicate: true
    },
    {
      id: 21,
      place: Place.EKEBERG,
      interviewTitle: '',
      seo: {
        title: 'Alltid stor interesse for bolig på Nordstrand og Bekkelaget',
        description:
          'Uavhengig av svingninger i markedet generelt, er det alltid stor interesse for boliger i denne delen av byen, Hanne Eika Klempe.'
      },
      card: {
        title: 'Alltid stor interesse for bolig på Nordstrand og Bekkelaget.',
        description:
          'Det er stadig et attraktivt område, uavhengig av svingninger i markedet, ifølge eiendomsmegler Hanne.',
        buttonLabel: 'Les mer'
      },
      title: 'Alltid stor interesse for bolig på Nordstrand og Bekkelaget',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Hanne Eika Klempe</p><h2 style="font-size: 28px;">Boligmiksen i området gjør at man kan bo her hele livet om man vil.</h2>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Hanne.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan har utviklingen vært den siste tiden?</h4>
        <p>De som kommer på visning nå for tiden er opptatt av kvalitet. Mange tar seg tid til å fordøye
        inntrykkene, og flere ber om ekstra visning for å være sikker på at de gjør en god handel. Det
        er tross alt en stor investering å kjøpe ny bolig. Uavhengig av svingninger i markedet
        generelt, er det alltid stor interesse for boliger i denne delen av byen.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>Tidligere var det tiltalende med oppussingsobjekter, hvor man kan sette sitt preg på boligen.
        Nå er folk mer bevisste på hva slags oppussing som behøves. Det henger sammen med nye
        tilstandsrapporter, som er mer konkrete og detaljerte – og dermed også gir større trygghet
        for boligkjøpere.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvem passer områdene for?</h4>
        <p>Boligmiksen i området gjør at man kan bo her hele livet om man vil. Man starter kanskje i en
        liten leilighet, oppgraderer til familiebolig, og går tilbake til leilighet når barna flytter ut.
        Lysløype og skøytebane om vinteren, og store, grønne friområder om sommeren er spesielt
        tiltalende for barnefamilier som ønsker kortreiste fritidsaktiviteter.</p>
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Ekebergparken_Hanne.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Hva vil skje i området de neste 5–10 årene?</h4>
      <p>Min oldefar bodde i byen, og hadde hytte her. Siden da har det skjedd veldig mye med
      utbygging av rekkehus og villaer. Fremover vil knutepunktene utvikles videre, med flere
      leiligheter og servicetilbud for fremtidige generasjoner med beboere.</p>   
      <h4 class="title-margin block"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
      <p>Som meglerforetak har vi 40 prosent markedsandel på eneboliger, tomannsboliger og
      rekkehus i området. Mange av meglerne bor her selv, og det er derfor enkelt for oss å forstå
      hva kundene er på jakt etter, og hvor man finner det.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Hanne.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Ekebergparken_Hanne.jpg',
      imageOverlay: '',
      slug: 'intervju21',
      link: '/kampanje/panorama/ekeberg/intervju21',
      brokerProfile: 'hanne_eika_klempe',
      duplicate: false
    },
    {
      id: 22,
      place: Place.NORDSTRAND,
      interviewTitle: '',
      seo: {
        title: '',
        description: ''
      },
      card: {
        title: 'Alltid stor interesse for bolig på Nordstrand og Bekkelaget.',
        description:
          'Det er stadig et attraktivt område, uavhengig av svingninger i markedet, ifølge eiendomsmegler Hanne.',
        buttonLabel: 'Les mer'
      },
      title: 'Alltid stor interesse for bolig på Nordstrand og Bekkelaget',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Hanne Eika Klempe</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Hanne.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan har utviklingen vært den siste tiden?</h4>
        <p>De som kommer på visning nå for tiden er opptatt av kvalitet. Mange tar seg tid til å fordøye
        inntrykkene, og flere ber om ekstra visning for å være sikker på at de gjør en god handel. Det
        er tross alt en stor investering å kjøpe ny bolig. Uavhengig av svingninger i markedet
        generelt, er det alltid stor interesse for boliger i denne delen av byen.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>Tidligere var det tiltalende med oppussingsobjekter, hvor man kan sette sitt preg på boligen.
        Nå er folk mer bevisste på hva slags oppussing som behøves. Det henger sammen med nye
        tilstandsrapporter, som er mer konkrete og detaljerte – og dermed også gir større trygghet
        for boligkjøpere.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hvem passer områdene for?</h4>
        <p>Boligmiksen i området gjør at man kan bo her hele livet om man vil. Man starter kanskje i en
        liten leilighet, oppgraderer til familiebolig, og går tilbake til leilighet når barna flytter ut.
        Lysløype og skøytebane om vinteren, og store, grønne friområder om sommeren er spesielt
        tiltalende for barnefamilier som ønsker kortreiste fritidsaktiviteter.</p>
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Ekebergparken_Hanne.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Hva vil skje i området de neste 5–10 årene?</h4>
      <p>Min oldefar bodde i byen, og hadde hytte her. Siden da har det skjedd veldig mye med
      utbygging av rekkehus og villaer. Fremover vil knutepunktene utvikles videre, med flere
      leiligheter og servicetilbud for fremtidige generasjoner med beboere.</p>       
      <h4 class="title-margin block"style=font-size:1.5rem>Hva gjør PrivatMegleren annerledes?</h4>
      <p>Som meglerforetak har vi 40 prosent markedsandel på eneboliger, tomannsboliger og
      rekkehus i området. Mange av meglerne bor her selv, og det er derfor enkelt for oss å forstå
      hva kundene er på jakt etter, og hvor man finner det.</p>
        `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Hanne.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Ekebergparken_Hanne.jpg',
      imageOverlay: '',
      slug: 'intervju21',
      link: '/kampanje/panorama/nordstrand/intervju21',
      brokerProfile: 'hanne_eika_klempe',
      duplicate: true
    }

    /*     {
      id: 23,
      place: Place.EKEBERG,
      interviewTitle: '',
      seo: {
        title: '',
        description: ''
      },
      card: {
        title: 'Det er rom for alle på Ekeberg og Bekkelaget',
        description:
          'Gjennom mange år som eiendomsmegler i samme område som han selv vokste opp, har Alexander gode forutsetninger for å mene at Bekkelaget passer for alle.',
        buttonLabel: 'Les mer'
      },
      title: 'Det er rom for alle på Ekeberg og Bekkelaget',
      video: 'https://player.vimeo.com/video/672745884?amp;loop=0&background=0',
      ingress: `<p>Alexander Moltubak Løken</p>`,
      quote: '',
      content: `
        <img class="inline-image" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Alexander.jpg" alt="intervju-bilde"  />
        <h4 class="title-margin"style=font-size:1.5rem>Hvordan har markedsutviklingen vært den siste tiden?</h4>
        <p>– Det er bra driv i markedet for tiden. Med stadig flere eiendommer som har kommet på markedet i løpet av forsommeren, er det nå flere valgalternativer for kjøperne. Det er stor interesse og godt oppmøte på visningene.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Har du lagt merke til noen trender?</h4>
        <p>– Muligheten for å jobbe hjemmefra er fremdeles viktig for mange. Trenden er at flere er opptatt av plass til det ekstra kontorrommet.</p>
        <h4 class="title-margin"style=font-size:1.5rem>Hva gjør at folk trives i området?</h4>
        <p>– Ekeberg og Bekkelaget er sentrumsnært, men er mye roligere. Mange av nabolagene har vid utsikt og de fleste har gode solforhold. Det er enkel tilgang til skog og mark, og samtidig kort vei til sjøen. Det gjør at området passer for barnefamilier så vel som for godt voksne.</p>
      
        `,
      contentTwo: `
      <img class="inline-image image-block-height" src="https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/ekebergsletta_Alexander.jpg" alt="intervju-bilde"  />
      <h4 class="title-margin block"style=font-size:1.5rem>Har du tips til friluftsmuligheter eller aktivitetstilbud i området?</h4>
      <p>– EKT Rideskole og husdyrpark på Ekebergsletta er et veldig fint tilbud, spesielt for barnefamilier. Jeg vokste opp i området selv, og har bare gode minner fra turer hit i barndommen. Ellers finner man også landets eldste frisbeegolfbane på Ekeberg.</p>       
      <h4 class="title-margin block"style=font-size:1.5rem>Hva vil skje i området de neste 5-10 årene?</h4>
      <p>– Flere vil bosette seg her i årene som kommer. Ny barnehage, barneskole og økt servicetilbud vil komme som en konsekvens av befolkningsøkningen. På lang sikt tror jeg det kan bli mindre biltrafikk og bedre kollektivtilbud.</p>
      <h4 class="title-margin block"style=font-size:1.5rem>Hvorfor skal jeg velge deg som megler?</h4>
      <p>– Du bør velge meg dersom du er opptatt av god faglig kvalitet gjennom hele prosessen. Mange ser det som ekstra tillitsvekkende at jeg har lokalkunnskapen på plass ettersom jeg vokste opp, og fremdeles bor i området selv.</p>
      `,

      image:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/Alexander.jpg',
      imageHighlight: '',
      imageSoMe:
        'https://cdn.reeltime.no/pm_assets/kampanjer/panorama/Ekeberg/ekebergsletta_Alexander.jpg',
      imageOverlay: '',
      slug: 'intervju23',
      link: '/kampanje/panorama/ekeberg/intervju23',
      brokerProfile: 'alexander_moltubak_løken',
      duplicate: false
    } */
  ];

  const getMain = () => {
    return main.find((item) => item.pathname === location.pathname) || null;
  };

  const getFirstInterview = (args: DefaultFilterArgs) => {
    return interviews.filter((item) => item.place === args.place)[0];
    /*  return interviews[Math.floor(Math.random() * interviews.length)]; */
  };

  /* Picking out all episodes that is not of id 0 */
  const getInterviews = (args: GetInterviewsFilterArgs) => {
    let filtered = [...interviews];
    if (args?.place) {
      filtered = filtered.filter((item) => item.place === args.place);
    }

    if (args?.excludeFirst) {
      filtered.splice(0, 1);
    }

    /* Removes all duplicate interviews */
    if (args?.duplicate) {
      filtered = filtered.filter((item) => item.duplicate === false);
    }

    if (args.excludeSlug) {
      filtered = filtered.filter((item) => item.slug !== args.excludeSlug);
    }
    return filtered;
  };

  /* Picking out a spesific episode based on link url string */
  const getInterview = () => {
    return interviews.find((item) => item.slug === params.slug) || null;
  };

  const getInterviewCountByType = (args: DefaultFilterArgs) => {
    let filteredInterviews = [...interviews];
    if (args?.place) {
      filteredInterviews = filteredInterviews.filter(
        (item) => item.place === args.place
      );
    }

    if (args.excludeFirst) {
      filteredInterviews.splice(0, 1);
    }

    if (args.excludeSlug) {
      filteredInterviews = filteredInterviews.filter(
        (item) => item.slug !== args.excludeSlug
      );
    }

    return filteredInterviews.length;
  };

  /* Showing 0-2 indexes in array (manipulating copied array with ...) */
  const getPaginatedInterviews = (args: GetPaginatedInterviewsFilterArgs) => {
    let paginatedInterviews = [...interviews];

    if (args.place) {
      /* Filter creates a copy of newly filtered array */
      paginatedInterviews = paginatedInterviews.filter(
        (item) => item.place === args.place
      );
    }

    /* if exclude first is defined, removes first interview in new copied array */
    if (args.excludeFirst) {
      paginatedInterviews.splice(0, 1);
    }

    if (args.excludeSlug) {
      paginatedInterviews = paginatedInterviews.filter(
        (item) => item.slug !== args.excludeSlug
      );
    }

    return [
      ...paginatedInterviews.splice(
        args.page * (args?.limit || 2),
        args?.limit || 2
      )
    ];
  };

  return (
    /* Implements episode functions in the context provider */
    <PanoramaContext.Provider
      value={{
        main: getMain,
        interviews: getInterviews,
        getInterview,
        getFirstInterview,
        getPaginatedInterviews,
        /*    getInterviewPageType, */
        getInterviewCountByType
      }}
    >
      {children}
    </PanoramaContext.Provider>
  );
};
